import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Send, MapPin, Grape, NotebookPen, Code2, Sparkles, Building2, CalendarDays, Camera, GraduationCap, Compass, Globe2, X } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PlanTag, LockedRow, hasPlanAccess } from "@/components/PlanTag";

export default function Home() {
  const { t } = useT();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [lesson, setLesson] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("geovinum_lesson_dismissed") === new Date().toDateString());
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !dismissed) api.get("/lessons/daily").then((r) => setLesson(r.data)).catch(() => {});
  }, [user, dismissed]);

  const dismiss = () => { localStorage.setItem("geovinum_lesson_dismissed", new Date().toDateString()); setDismissed(true); };

  const submit = (text) => {
    const value = (text ?? q).trim();
    if (!value) return;
    navigate(`/chat?q=${encodeURIComponent(value)}`);
  };

  const prompts = [0, 1, 2, 3, 4, 5].map((i) => t(`home.prompts.${i}`));

  const isAmateurPlus = hasPlanAccess(user, "amateur");

  const shortcuts = [
    { to: "/appellations", label: t("nav.appellations"), icon: MapPin, plan: null },
    { to: "/cepages", label: t("nav.cepages"), icon: Grape, plan: null },
    { to: "/producers", label: t("nav.producers"), icon: Building2, plan: null },
    { to: "/events", label: t("nav.events"), icon: CalendarDays, plan: null },
    { to: "/explorer", label: "Vision", icon: Camera, plan: "amateur" },
    { to: "/discover", label: "Explorer", icon: Compass, plan: null },
    { to: "/carnet", label: t("nav.carnet"), icon: NotebookPen, plan: "amateur" },
    { to: "/passport", label: "Passeport", icon: Globe2, plan: "amateur" },
  ];

  return (
    <div data-testid={HOME.hero} className="min-h-[calc(100dvh_-_var(--m-header-h)_-_var(--m-tabs-h))] md:min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-5 md:px-10 py-10">
        <div className="w-full max-w-2xl">
          {/* Daily lesson banner */}
          {user && lesson && !dismissed && (
            <div className="mb-8 p-4 rounded-2xl bg-gradient-to-r from-[#FCE4ED] to-[#FBF7F3] border border-[#E5276A]/20 flex items-start gap-3 animate-fade-up" data-testid="daily-lesson-banner">
              <div className="w-9 h-9 rounded-lg bg-[#E5276A] flex items-center justify-center shrink-0"><GraduationCap className="w-4 h-4 text-white" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">Micro-cours du jour</div>
                <div className="font-display text-lg text-[#1F3A5F] mt-0.5 truncate">{lesson.title}</div>
                {lesson.subtitle && <div className="text-xs text-[#4A5568] truncate">{lesson.subtitle}</div>}
                <button onClick={() => submit(`Donne-moi le cours du jour : ${lesson.title}. ${lesson.body || ''}`)} className="mt-2 text-xs font-bold text-[#E5276A] hover:underline" data-testid="daily-lesson-open">Lire le cours →</button>
              </div>
              <button onClick={dismiss} aria-label="dismiss" className="p-1 text-[#8B95A1] hover:text-[#1F3A5F]"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="flex flex-col items-center mb-6 animate-fade-up">
            <img
              src="/brand/audrey-ai.png"
              alt="geoVINUM AI"
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-4 ring-[#FCE4ED] shadow-lg mb-3"
              data-testid="home-audrey-portrait"
            />
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FCE4ED] text-[#E5276A]">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[11px] font-bold tracking-[0.18em] uppercase">geoVINUM AI</span>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl text-[#1F3A5F] text-center leading-[1.1] animate-fade-up px-2 text-balance" style={{ animationDelay: "60ms" }}>
            {t("home.greeting")}
          </h1>
          <p className="mt-5 text-center text-[15px] md:text-base text-[#4A5568] max-w-xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
            {t("home.sub")}
          </p>

          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="mt-10 animate-fade-up" style={{ animationDelay: "180ms" }}>
            <div className="flex items-center gap-2 bg-white border border-[#1F3A5F]/15 focus-within:border-[#E5276A] rounded-2xl px-4 py-3 shadow-sm hover:shadow-md transition-all">
              <input data-testid={HOME.searchInput} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("home.placeholder")}
                className="flex-1 bg-transparent outline-none text-base placeholder:text-[#8B95A1] text-[#1F3A5F] px-2 py-2" autoFocus />
              <button data-testid={HOME.sendBtn} type="submit" aria-label="send" disabled={!q.trim()}
                className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#E5276A] hover:bg-[#C01661] disabled:bg-[#E5276A]/30 text-white transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>

          <div className="mt-5 flex flex-wrap justify-center gap-2 animate-fade-up" style={{ animationDelay: "240ms" }}>
            {prompts.map((p, i) => (
              <button key={p} data-testid={HOME.promptChip(i)} onClick={() => submit(p)}
                className="px-3.5 py-1.5 rounded-full border border-[#1F3A5F]/12 bg-white hover:bg-[#FBF7F3] hover:border-[#E5276A]/40 text-[13px] text-[#4A5568] hover:text-[#1F3A5F] transition-colors">{p}</button>
            ))}
          </div>

          <div className="mt-12 animate-fade-up" style={{ animationDelay: "300ms" }}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold text-center mb-4">{t("home.shortcuts.title")}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {shortcuts.map(({ to, label, icon: Icon, plan }) => {
                const locked = plan && !hasPlanAccess(user, plan);
                const target = locked ? "/pricing" : to;
                return (
                  <LockedRow key={to} locked={locked}>
                    <Link to={target} data-testid={`home-shortcut-${to.replace("/", "")}`}
                          className="app-card p-4 flex flex-col items-start gap-2 relative">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#FCE4ED]">
                        <Icon className="w-4 h-4 text-[#E5276A]" />
                      </div>
                      <span className="text-sm font-bold text-[#1F3A5F]">{label}</span>
                      {plan && (
                        <span className="absolute top-2 right-2">
                          <PlanTag plan={plan} />
                        </span>
                      )}
                    </Link>
                  </LockedRow>
                );
              })}
            </div>
          </div>

          <p className="mt-10 text-center text-[11px] text-[#8B95A1]">{t("home.disclaimer")}</p>
        </div>
      </div>
    </div>
  );
}
